import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { detectLocationFromBirthData, convertINRtoUSD } from '../utils/locationUtils'

export interface PricingInfo {
  price: number
  currency: 'INR' | 'USD'
  currencySymbol: string
}

export interface SubscriptionPlan {
  id: string
  name: string
  description: string
  price: number // Base price in INR
  duration: number
  durationUnit: 'days' | 'months' | 'years'
  features: string[]
  popular?: boolean
  currency: 'INR' | 'USD'
  rraasiCoins: number // RRAASI coins included with this plan
  // Dynamic pricing based on location
  getLocationBasedPrice: (birthData?: any) => PricingInfo
}

export interface Subscription {
  id: string
  planId: string
  status: 'active' | 'expired' | 'cancelled'
  startDate: Date
  endDate: Date
  paymentId?: string
  amount: number
  currency: 'INR' | 'USD'
  rraasiCoinsAllocated: number // RRAASI coins allocated with this subscription
}

export interface UsageStats {
  chatCalls: number
  compatibilityCalls: number
  lastResetDate: string // ISO date string
}

interface SubscriptionState {
  currentSubscription: Subscription | null
  availablePlans: SubscriptionPlan[]
  usageStats: UsageStats
  isLoading: boolean
  error: string | null
  userLocation: 'INR' | 'USD' | null
  setCurrentSubscription: (subscription: Subscription | null) => void
  setAvailablePlans: (plans: SubscriptionPlan[]) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  setUserLocation: (location: 'INR' | 'USD') => void
  incrementChatCalls: () => void
  incrementCompatibilityCalls: () => void
  resetUsageStats: () => void
  isSubscriptionActive: () => boolean
  getActivePlan: () => SubscriptionPlan | null
  hasFreeChatCallsRemaining: () => boolean
  hasFreeCompatibilityCallsRemaining: () => boolean
  getRemainingChatCalls: () => number
  getRemainingCompatibilityCalls: () => number
  getLocationBasedPlans: (birthData?: any) => SubscriptionPlan[]
  allocateRraasiCoins: (planId: string) => void
  FREE_TIER_LIMIT: number
}

// Location-based pricing function
function getLocationBasedPricing(inrPrice: number, birthData?: { placeOfBirth?: string, latitude?: number, longitude?: number }): PricingInfo {
  if (!birthData || (!birthData.placeOfBirth && !birthData.latitude && !birthData.longitude)) {
    return { price: inrPrice, currency: 'INR', currencySymbol: '₹' }
  }

  const locationInfo = detectLocationFromBirthData(birthData)
  
  if (locationInfo.currency === 'USD') {
    const usdPrice = convertINRtoUSD(inrPrice)
    return { price: usdPrice, currency: 'USD', currencySymbol: '$' }
  }
  
  return { price: inrPrice, currency: 'INR', currencySymbol: '₹' }
}

export const useSubscriptionStore = create<SubscriptionState>()(
  persist(
    (set, get) => ({
      currentSubscription: null,
      availablePlans: [
        {
          id: 'starter_7',
          name: 'Starter Pack',
          description: 'Perfect for trying out our services',
          price: 49, // Base price in INR
          duration: 7,
          durationUnit: 'days' as const,
          rraasiCoins: 200, // 200 RRAASI coins
          features: [
            '200 RRAASI Coins',
            '20 Free Chat Calls',
            '5 Compatibility Checks',
            'Basic Birth Chart',
            'Daily Horoscope',
            'Email Support'
          ],
          popular: false,
          currency: 'INR',
          getLocationBasedPrice: (birthData?: any) => getLocationBasedPricing(49, birthData)
        },
        {
          id: 'premium_30',
          name: 'Premium Pack',
          description: 'Most popular choice for regular users',
          price: 199, // Base price in INR
          duration: 30,
          durationUnit: 'days' as const,
          rraasiCoins: 800, // 800 RRAASI coins
          features: [
            '800 RRAASI Coins',
            'Unlimited Chat Calls',
            'Unlimited Compatibility Checks',
            'Advanced Birth Chart',
            'Detailed Horoscope',
            'Priority Support',
            'Personalized Insights'
          ],
          popular: true,
          currency: 'INR',
          getLocationBasedPrice: (birthData?: any) => getLocationBasedPricing(199, birthData)
        },
        {
          id: 'cosmic_90',
          name: 'Cosmic Pack',
          description: 'Ultimate experience for serious users',
          price: 499, // Base price in INR
          duration: 90,
          durationUnit: 'days' as const,
          rraasiCoins: 2500, // 2500 RRAASI coins
          features: [
            '2500 RRAASI Coins',
            'Everything in Premium',
            'Advanced Analytics',
            'Custom Reports',
            'Expert Consultation',
            'VIP Support',
            'Exclusive Content'
          ],
          popular: false,
          currency: 'INR',
          getLocationBasedPrice: (birthData?: any) => getLocationBasedPricing(499, birthData)
        },
        {
          id: 'annual_365',
          name: 'Annual Master',
          description: 'Best value for long-term commitment',
          price: 1499, // Base price in INR
          duration: 365,
          durationUnit: 'days' as const,
          rraasiCoins: 10000, // 10000 RRAASI coins
          features: [
            '10000 RRAASI Coins',
            'Everything in Cosmic',
            'Lifetime Updates',
            'Family Account',
            'Dedicated Support',
            'Exclusive Events',
            'Early Access Features'
          ],
          popular: false,
          currency: 'INR',
          getLocationBasedPrice: (birthData?: any) => getLocationBasedPricing(1499, birthData)
        }
      ],
      usageStats: {
        chatCalls: 0,
        compatibilityCalls: 0,
        lastResetDate: new Date().toISOString()
      },
      isLoading: false,
      error: null,
      userLocation: null,
      FREE_TIER_LIMIT: 5,
      
      setCurrentSubscription: (subscription: Subscription | null) => {
        set({ currentSubscription: subscription })
        
        // If subscription is active, allocate RRAASI coins
        if (subscription && subscription.status === 'active') {
          get().allocateRraasiCoins(subscription.planId)
        }
      },
      
      setAvailablePlans: (plans: SubscriptionPlan[]) => set({ availablePlans: plans }),
      setLoading: (loading: boolean) => set({ isLoading: loading }),
      setError: (error: string | null) => set({ error }),
      setUserLocation: (location: 'INR' | 'USD') => set({ userLocation: location }),
      
      incrementChatCalls: () => set((state) => ({
        usageStats: { ...state.usageStats, chatCalls: state.usageStats.chatCalls + 1 }
      })),
      
      incrementCompatibilityCalls: () => set((state) => ({
        usageStats: { ...state.usageStats, compatibilityCalls: state.usageStats.compatibilityCalls + 1 }
      })),
      
      resetUsageStats: () => set((state) => ({
        usageStats: { ...state.usageStats, lastResetDate: new Date().toISOString() }
      })),
      
      isSubscriptionActive: () => {
        const subscription = get().currentSubscription
        if (!subscription || subscription.status !== 'active') return false
        
        const now = new Date()
        const endDate = new Date(subscription.endDate)
        return now <= endDate
      },
      
      getActivePlan: () => {
        const subscription = get().currentSubscription
        if (!subscription || !get().isSubscriptionActive()) return null
        
        return get().availablePlans.find(plan => plan.id === subscription.planId) || null
      },
      
      hasFreeChatCallsRemaining: () => {
        const subscription = get().currentSubscription
        if (subscription && get().isSubscriptionActive()) return true
        
        const usageStats = get().usageStats
        const lastReset = new Date(usageStats.lastResetDate)
        const now = new Date()
        const daysSinceReset = Math.floor((now.getTime() - lastReset.getTime()) / (1000 * 60 * 60 * 24))
        
        if (daysSinceReset >= 30) {
          get().resetUsageStats()
          return true
        }
        
        return usageStats.chatCalls < get().FREE_TIER_LIMIT
      },
      
      hasFreeCompatibilityCallsRemaining: () => {
        const subscription = get().currentSubscription
        if (subscription && get().isSubscriptionActive()) return true
        
        const usageStats = get().usageStats
        const lastReset = new Date(usageStats.lastResetDate)
        const now = new Date()
        const daysSinceReset = Math.floor((now.getTime() - lastReset.getTime()) / (1000 * 60 * 60 * 24))
        
        if (daysSinceReset >= 30) {
          get().resetUsageStats()
          return true
        }
        
        return usageStats.compatibilityCalls < get().FREE_TIER_LIMIT
      },
      
      getRemainingChatCalls: () => {
        const subscription = get().currentSubscription
        if (subscription && get().isSubscriptionActive()) return Infinity
        
        const usageStats = get().usageStats
        const lastReset = new Date(usageStats.lastResetDate)
        const now = new Date()
        const daysSinceReset = Math.floor((now.getTime() - lastReset.getTime()) / (1000 * 60 * 60 * 24))
        
        if (daysSinceReset >= 30) {
          get().resetUsageStats()
          return get().FREE_TIER_LIMIT
        }
        
        return Math.max(0, get().FREE_TIER_LIMIT - usageStats.chatCalls)
      },
      
      getRemainingCompatibilityCalls: () => {
        const subscription = get().currentSubscription
        if (subscription && get().isSubscriptionActive()) return Infinity
        
        const usageStats = get().usageStats
        const lastReset = new Date(usageStats.lastResetDate)
        const now = new Date()
        const daysSinceReset = Math.floor((now.getTime() - lastReset.getTime()) / (1000 * 60 * 60 * 24))
        
        if (daysSinceReset >= 30) {
          get().resetUsageStats()
          return get().FREE_TIER_LIMIT
        }
        
        return Math.max(0, get().FREE_TIER_LIMIT - usageStats.compatibilityCalls)
      },
      
      getLocationBasedPlans: (birthData?: any) => {
        return get().availablePlans.map(plan => ({
          ...plan,
          ...plan.getLocationBasedPrice(birthData)
        }))
      },
      
      allocateRraasiCoins: (planId: string) => {
        const plan = get().availablePlans.find(p => p.id === planId)
        if (!plan) return
        
        // Import and use the RRAASI coin store
        import('../stores/rraasiCoinStore').then(({ useRraasiCoinStore }) => {
          const coinStore = useRraasiCoinStore.getState()
          coinStore.addCoins(
            plan.rraasiCoins,
            'subscription',
            `${plan.name} subscription activated!`,
            'subscription-activation',
            planId
          )
        }).catch(console.error)
      }
    }),
    {
      name: 'subscription-storage',
      partialize: (state) => ({
        currentSubscription: state.currentSubscription,
        usageStats: state.usageStats,
        userLocation: state.userLocation
      })
    }
  )
) 